import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import { getCurrentOnboarding } from '../services/onboarding'
import { getCurrentUser, isUserProfileComplete } from '../services/user'
import { useUserStore } from '../stores/user'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
  {
    path: '/completed-register',
    component: () => import('../layouts/CompletedRegisterLayout.vue'),
    children: [
      {
        name: 'register',
        path: '',
        component: () => import('../pages/register/CompletedRegisterPage.vue'),
        meta: { requiresAuth: true },
      },
    ],
  },

  /*
   * Onboarding independiente.
   * No utiliza AppLayout, por lo tanto no muestra
   * sidebar, navbar ni contenido del dashboard.
   */
  {
    name: 'onboarding',
    path: '/onboarding',
    component: () => import('../pages/onboarding/OnboardingPage.vue'),
    meta: {
      requiresAuth: true,
    },
  },

  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    meta: { requiresAuth: true },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/HomePage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'workspace',
        path: 'workspace',
        component: () => import('../pages/workspace/WorkspacePage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'facturacion',
        path: 'facturacion',
        component: () => import('../pages/facturacion/facturacion.vue'),
        meta: { requiresAuth: true },
      },

      {
        name: 'finanzas',
        path: 'finanzas',
        component: () => import('../pages/finanzas/finanzas.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'administracion',
        path: 'administracion',
        component: () => import('../pages/administracion/Administracion.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/users/UsersPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'products',
        path: 'products',
        component: () => import('../pages/products/ProductsPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'ventas',
        path: 'ventas',
        component: () => import('../pages/ventas/VentasPage.vue'),
        meta: { requiresAuth: true, billing: true },
      },
      {
        name: 'generar-factura',
        path: 'generar-factura',
        component: () => import('../pages/ventas/GenerateInvoice.vue'),
        meta: { requiresAuth: true, billing: true },
      },
      {
        name: 'compras',
        path: 'compras',
        component: () => import('../pages/purchase/PurchasePage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'conciliar-sri',
        path: 'compras/conciliar-sri',
        component: () => import('../pages/purchase/SriReconcilePage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'reports',
        path: 'reports',
        component: () => import('../pages/reports/ReportsPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'budgets',
        path: '/budgets',
        component: () => import('../pages/budgets/BudgetPage.vue'),
        meta: { requiresAuth: true, menu: 'PERSONAL' },
      },
      {
        name: 'funds',
        path: '/funds',
        component: () => import('../pages/funds/FundPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'accounts',
        path: '/accounts',
        component: () => import('../pages/accounts/AccountPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'reminders',
        path: '/reminders',
        component: () => import('../pages/reminders/ReminderPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'ingresos',
        path: 'ingresos',
        component: () => import('../pages/ingresos/IngresosPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'egresos',
        path: 'egresos',
        component: () => import('../pages/egresos/EgresosPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'categories',
        path: 'categories',
        redirect: { name: 'categories-egresos' },
        meta: { requiresAuth: true, menu: 'BUSINESS' },
      },
      {
        name: 'categories-egresos',
        path: 'categories/egresos',
        component: () => import('../pages/categories/CategoriesPage.vue'),
        meta: { requiresAuth: true, menu: 'BUSINESS' },
      },
      {
        name: 'categories-ingresos',
        path: 'categories/ingresos',
        component: () => import('../pages/categories/CategoriesPage.vue'),
        meta: { requiresAuth: true, menu: 'BUSINESS' },
      },
      {
        name: 'notes',
        path: '/notes',
        component: () => import('../pages/notes/NotesPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'tax',
        path: '/tax',
        component: () => import('../pages/tax/Tax.vue'),
        meta: { requiresAuth: true },
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
        meta: { requiresAuth: false },
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
        meta: { requiresAuth: false },
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
        meta: { requiresAuth: false },
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
        meta: { requiresAuth: false },
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
    meta: { requiresAuth: false },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

export function isMenuRouteVisible(name: string | symbol | null | undefined, personal: boolean) {
  const menu = router.getRoutes().find((item) => item.name === name)?.meta.menu
  if (menu === 'PERSONAL') return personal
  if (menu === 'BUSINESS') return !personal
  return true
}

export function isBillingMenuVisible(
  name: string | symbol | null | undefined,
  billingEnabled: boolean,
  personal: boolean,
) {
  const billing = router.getRoutes().find((item) => item.name === name)?.meta.billing
  if (billing) return billingEnabled && !personal
  return true
}
export function initRouterGuards(auth: import('firebase/auth').Auth) {
  router.beforeEach(async (to, from, next) => {
    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)

    const currentUser = auth.currentUser

    /*
     * Una ruta privada requiere autenticación.
     */
    if (requiresAuth && !currentUser) {
      return next({
        name: 'login',
      })
    }

    /*
     * Sin sesión, permitimos las rutas públicas.
     */
    if (!currentUser) {
      return next()
    }

    try {
      const userStore = useUserStore()

      /*
       * Carga inicial:
       *
       * 1. Datos del usuario.
       * 2. Estado del onboarding.
       *
       * Se realiza una sola vez mientras la aplicación
       * permanezca abierta.
       */
      if (!userStore.onboardingLoaded) {
        const [profileResponse, backendOnboarding] = await Promise.all([getCurrentUser(), getCurrentOnboarding()])

        const backendProfile = profileResponse?.data?.user ?? null

        if (!backendProfile) {
          throw new Error('No se pudo cargar el usuario.')
        }

        if (!backendOnboarding) {
          throw new Error('No se pudo cargar el onboarding.')
        }

        userStore.setApiUser({
          ...userStore.apiUser,
          ...backendProfile,
        })

        userStore.setOnboarding(backendOnboarding)
      }
      const profile = userStore.apiUser

      const profileComplete = isUserProfileComplete(profile)

      /*
       * PRIMERA VERIFICACIÓN:
       * nombre, apellido y teléfono.
       */
      if (!profileComplete) {
        if (to.name === 'register') {
          return next()
        }

        return next({
          name: 'register',
        })
      }

      /*
       * SEGUNDA VERIFICACIÓN:
       * onboarding.
       */
      const onboardingComplete = userStore.onboarding?.currentStep === 'COMPLETED'

      /*
       * Perfil completo, pero onboarding pendiente.
       * No puede entrar al dashboard ni a otras páginas.
       */
      if (!onboardingComplete) {
        if (to.name === 'onboarding') {
          return next()
        }

        return next({
          name: 'onboarding',
        })
      }

      /*
       * Inicio es la misma ruta. El componente elige el resumen
       * de finanzas personales o el dashboard del negocio.
       */
      if (!userStore.workspaces.length) {
        await userStore.me()
      }

      const homeRouteName = 'dashboard'

      /*
       * Perfil y onboarding completos.
       * No puede regresar al registro, onboarding
       * ni páginas de autenticación.
       */
      if (to.name === 'register' || to.name === 'onboarding' || to.path.startsWith('/auth')) {
        return next({
          name: homeRouteName,
        })
      }

      if (userStore.isPersonalWorkspace && (to.name === 'finanzas' || to.name === 'tax')) {
        return next({
          name: 'dashboard',
        })
      }

      /*
       * En escritorio, los workspaces business usan el dashboard
       * de negocio y las vistas separadas de Ingresos/Egresos.
       * En móvil se les muestra la misma página de Finanzas
       * que a los workspaces personales, así que no se redirige.
       */
      const isMobileViewport = window.innerWidth <= 820
      if (!isMenuRouteVisible(to.name, userStore.isPersonalWorkspace)) {
        return next({
          name: homeRouteName,
        })
      }

      if (!isBillingMenuVisible(to.name, userStore.billingEnabled, userStore.isPersonalWorkspace)) {
        return next({
          name: 'compras',
        })
      }

      if (userStore.isBusinessWorkspace && to.name === 'finanzas' && !isMobileViewport) {
        return next({
          name: 'dashboard',
        })
      }

      if (to.name === 'settings' && !(userStore.isBusinessWorkspace && userStore.billingEnabled)) {
        return next({
          name: 'dashboard',
        })
      }

      if (!userStore.billingEnabled && to.name === 'users') {
        return next({
          name: 'dashboard',
        })
      }

      if (userStore.isPersonalWorkspace && (to.name === 'users' || to.name === 'products' || to.name === 'administracion')) {
        return next({
          name: 'dashboard',
        })
      }

      return next()
    } catch (error) {
      console.error('Error cargando la sesión:', error)

      return next({
        name: 'login',
      })
    }
  })
}

export default router
